import React from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Avatar from '@material-ui/core/Avatar'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemAvatar from '@material-ui/core/ListItemAvatar'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'

import { AVATAR_SOURCE, lessonContentDomain } from '../../common/utils'
import { CATEGORIES } from './categories'
import { documentTotal, foldersOf, solutionsTotal } from './folders'
import { HEADER_HEIGHT } from './layout'

const useStyles = makeStyles(theme => ({
  rail: {
    width: 280,
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    // Q-E: white content, charcoal rail. custom.background is the app-shell
    // ground (#2B2B2B in both palettes); the rail wears it deliberately so
    // navigation reads as dashboard chrome against the white content.
    backgroundColor: theme.palette.custom.background,
    color: theme.palette.common.white,
    textAlign: 'left',
  },
  // Fixed to the shared height so this rule, the folder rail's and the
  // content pane's all land on one line across the page.
  railHead: {
    height: HEADER_HEIGHT,
    boxSizing: 'border-box',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    padding: theme.spacing(0, 2),
    borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
  },
  railMeta: {
    color: 'rgba(255, 255, 255, 0.65)',
  },
  railList: {
    flexGrow: 1,
    overflowY: 'auto',
    padding: theme.spacing(1),
  },
  // MUI's hover and selected overlays come from `palette.action`, which is
  // tuned for dark-on-light. On the charcoal rail they land at roughly zero
  // contrast, so both states are restated here in light-on-dark. The component
  // still owns the behaviour; only the two colours are ours.
  pack: {
    borderRadius: 6,
    border: '1px solid transparent',
    marginBottom: theme.spacing(0.5),
    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.07)' },
    '&$selected, &$selected:hover': {
      borderColor: theme.palette.secondary.main,
      backgroundColor: 'rgba(76, 175, 80, 0.18)',
    },
  },
  selected: {},
  avatar: { width: 36, height: 36 },
  avatarSlot: { minWidth: 48 },
}))

/**
 * The teacher's entitled packs. This list IS the entitlement — a pack the
 * teacher does not hold never appears, so there is nothing here to click.
 */
function PackRail({ packs, selectedPackId, onSelectPack }) {
  const classes = useStyles()

  return (
    <div className={classes.rail}>
      <div className={classes.railHead}>
        <Typography variant='subtitle1'>{'Your curriculum'}</Typography>
      </div>
      <List className={classes.railList} disablePadding aria-label='Your curriculum'>
        {packs.map((pack) => {
          // Counted from `pack.folders` — the published manifest — the same
          // source as the heading over the content pane. The legacy
          // `categories` counts read the bucket by prefix and still see
          // wave-1 objects the page never shows, so the two disagreed.
          const folders = foldersOf(pack, CATEGORIES)
          const solutions = solutionsTotal(folders)
          const documents = documentTotal(folders)
          return (
            <ListItem
              key={pack.id}
              button
              selected={pack.id === selectedPackId}
              classes={{ root: classes.pack, selected: classes.selected }}
              onClick={() => onSelectPack(pack.id)}
            >
              {pack.avatar &&
                <ListItemAvatar className={classes.avatarSlot}>
                  <Avatar
                    className={classes.avatar}
                    variant='rounded'
                    alt=''
                    src={`${lessonContentDomain}${AVATAR_SOURCE}${pack.avatar}`}
                  />
                </ListItemAvatar>}
              <ListItemText
                disableTypography
                primary={
                  <Typography variant='body2' component='div'>
                    {pack.name}
                  </Typography>
                }
                secondary={
                  <Typography variant='caption' component='div' className={classes.railMeta}>
                    {`${solutions} solutions · ${documents} documents`}
                  </Typography>
                }
              />
            </ListItem>
          )
        })}
      </List>
    </div>
  )
}

export { PackRail }
